import { useState, useEffect, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth-context";
import { LanguageSwitcher } from "@/components/language-switcher";
import {
  useAdminCountUnhandledContacts,
  useAdminCountPendingVatInvoices,
} from "@workspace/api-client-react";

const ICON_STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function IcoClients() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IcoReconcile() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
function IcoReports() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M3 3v18h18" />
      <rect x="7" y="11" width="3" height="6" />
      <rect x="13" y="7" width="3" height="10" />
    </svg>
  );
}
function IcoMail() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  );
}
function IcoReferrals() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <circle cx="9" cy="7" r="4" />
      <path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  );
}
function IcoAffiliates() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
function IcoLocation() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function IcoSettings() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

interface NavItem {
  name: string;
  href: string;
  icon: ReactNode;
  badgeCount?: number;
}

const NAV: NavItem[] = [
  { name: "nav.clients", href: "/admin/clients", icon: <IcoClients /> },
  { name: "nav.reconciliation", href: "/admin/reconciliation", icon: <IcoReconcile /> },
  { name: "nav.contact", href: "/admin/contact", icon: <IcoMail /> },
  { name: "nav.reports", href: "/admin/reports", icon: <IcoReports /> },
  { name: "nav.referrals", href: "/admin/referrals", icon: <IcoReferrals /> },
  { name: "nav.affiliates", href: "/admin/affiliates", icon: <IcoAffiliates /> },
  { name: "nav.adaptiveLocation", href: "/admin/adaptive-location", icon: <IcoLocation /> },
  { name: "nav.settings", href: "/admin/settings", icon: <IcoSettings /> },
];

const SECTION_LABEL_STYLE: React.CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "1.4px",
  color: "#8C7E70",
  padding: "16px 8px 6px",
  fontWeight: 600,
};

const NAV_ACTIVE_STYLE: React.CSSProperties = {
  background: "linear-gradient(135deg,#FF8A2B,#FF5900)",
  color: "#fff",
  boxShadow: "0 10px 22px rgba(255,89,0,.35),inset 0 1px 0 rgba(255,255,255,.18)",
};

export function AdminShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("admin");
  const [location, setLocation] = useLocation();
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location]);

  const handleLogout = async () => {
    await logout();
    setLocation("/");
  };

  const isActive = (href: string) => location === href || location.startsWith(href + "/");
  const initial = (user?.displayName || user?.username || "?").charAt(0).toUpperCase();

  const { data: unhandled } = useAdminCountUnhandledContacts({
    query: { refetchInterval: 60_000 } as never,
  });
  const unhandledCount = unhandled?.count ?? 0;

  const { data: pendingVat } = useAdminCountPendingVatInvoices({
    query: { refetchInterval: 60_000 } as never,
  });
  const pendingVatCount = pendingVat?.count ?? 0;

  const navItems = NAV.map((item) => {
    if (item.href === "/admin/contact") {
      return { ...item, badgeCount: unhandledCount };
    }
    if (item.href === "/admin/reconciliation") {
      return { ...item, badgeCount: pendingVatCount };
    }
    return item;
  });

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.href);
    const badge = item.badgeCount ?? 0;
    return (
      <Link key={item.name} href={item.href} asChild>
        <a
          data-testid={`link-adminnav-${item.href.replace(/\//g, "-").replace(/^-/, "")}`}
          aria-current={active ? "page" : undefined}
          className="nav-row group"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            padding: "9px 12px",
            borderRadius: 11,
            fontSize: 13.5,
            color: "#F3ECE3",
            marginBottom: 2,
            cursor: "pointer",
            transition: ".15s",
            ...(active ? NAV_ACTIVE_STYLE : {}),
          }}
        >
          {item.icon}
          {t(item.name)}
          {badge > 0 && (
            <span
              data-testid={
                item.href === "/admin/reconciliation"
                  ? "badge-pending-vat-invoices"
                  : "badge-unhandled-contacts"
              }
              style={{
                marginLeft: "auto",
                fontSize: 11,
                fontWeight: 700,
                minWidth: 20,
                height: 20,
                padding: "0 6px",
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: active ? "#FF5900" : "#fff",
                background: active
                  ? "rgba(255,255,255,.9)"
                  : "linear-gradient(135deg,#FF8A2B,#FF5900)",
              }}
            >
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </a>
      </Link>
    );
  };

  return (
    <div className="app-grid grid h-screen" style={{ gridTemplateColumns: "240px 1fr" }}>
      {drawerOpen && (
        <button
          type="button"
          aria-label={t("shell.closeMenu")}
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-[150] lg:hidden"
          style={{ background: "rgba(0,0,0,.55)", backdropFilter: "blur(2px)" }}
        />
      )}

      <aside
        className={`navside flex flex-col ${drawerOpen ? "open" : ""}`}
        style={{
          borderRight: "1px solid rgba(255,255,255,.1)",
          padding: "18px 0 12px",
          background: "linear-gradient(180deg,rgba(56,42,32,.4),rgba(34,26,20,.2))",
          backdropFilter: "blur(12px)",
          minHeight: 0,
        }}
      >
        <div
          className="flex items-center"
          style={{
            padding: "0 18px 16px",
            gap: 11,
            borderBottom: "1px solid rgba(255,255,255,.1)",
            marginBottom: 10,
          }}
        >
          <img
            src="/app-icon.png"
            alt="Creative Adapt"
            style={{ width: 30, height: 30, borderRadius: 9, objectFit: "cover" }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: ".2px" }}>{t("shell.brand")}</div>
            <div style={{ fontSize: 10.5, color: "#8C7E70" }}>Creative Adapt</div>
          </div>
        </div>

        <div className="nav-scroll flex-1 overflow-y-auto" style={{ minHeight: 0, padding: "0 12px" }}>
          <div style={{ ...SECTION_LABEL_STYLE, paddingTop: 12 }}>{t("shell.operations")}</div>
          {navItems.map((item) => renderNavItem(item))}
        </div>

        <div style={{ flex: "none", padding: "8px 12px 0" }}>
          <div className="flex justify-center" style={{ padding: "4px 6px" }}>
            <LanguageSwitcher className="text-[#F3ECE3]" />
          </div>
          <div style={{ height: 1, background: "rgba(255,255,255,.1)", margin: "6px 2px 10px" }} />
          <div className="flex items-center" style={{ gap: 10, padding: "4px 6px" }}>
            <div
              className="flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#FF8A2B,#FF5900)",
                flex: "none",
                boxShadow: "0 5px 12px rgba(255,89,0,.32)",
                fontSize: 13,
                fontWeight: 600,
                color: "#fff",
              }}
            >
              {initial}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                data-testid="text-admin-name"
              >
                {user?.displayName ?? t("shell.brand")}
              </div>
              <div style={{ fontSize: 11, color: "#8C7E70" }}>
                {t("shell.systemAdmin")}
                {user?.username ? ` · @${user.username}` : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title={t("shell.logout")}
              data-testid="button-admin-logout"
              className="logout-btn flex items-center justify-center"
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                border: "1px solid rgba(255,255,255,.12)",
                background: "rgba(255,255,255,.05)",
                flex: "none",
                color: "#8C7E70",
                cursor: "pointer",
                transition: ".15s",
              }}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      <main className="flex flex-col" style={{ minHeight: 0, position: "relative" }}>
        <div
          className="mtop flex items-center lg:hidden"
          style={{
            gap: 12,
            padding: "10px 14px",
            borderBottom: "1px solid rgba(255,255,255,.08)",
            background: "linear-gradient(160deg,rgba(30,25,21,.94),rgba(16,13,11,.9))",
            flex: "none",
            position: "relative",
            zIndex: 60,
          }}
        >
          <button
            type="button"
            aria-label={t("shell.openMenu")}
            onClick={() => setDrawerOpen(true)}
            className="flex items-center justify-center"
            style={{
              width: 40,
              height: 40,
              borderRadius: 11,
              border: "1px solid rgba(255,255,255,.12)",
              background: "rgba(255,255,255,.05)",
              cursor: "pointer",
              color: "#F3ECE3",
              flex: "none",
            }}
          >
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <span style={{ fontWeight: 700, fontSize: 14 }}>{t("shell.brand")}</span>
          <LanguageSwitcher className="ml-auto text-[#F3ECE3]" />
        </div>

        <div
          className="scroll-pad flex-1 overflow-y-auto px-[15px] pt-[14px] pb-20 lg:px-[34px] lg:py-[26px]"
          style={{ minHeight: 0 }}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
